/**
 * Super-admin editor for all 6 legal/compliance pages — Terms, Privacy,
 * Refunds, Digital Delivery, Contact and Pricing intro. Also includes
 * an inbox of submissions from the public Contact form.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, RotateCcw, Save, Inbox, Eye } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { PageHeader } from "@/components/Ui";
import { renderMarkdown } from "@/lib/markdown";

const SLUG_LABELS = {
    terms:    "Terms & Conditions",
    privacy:  "Privacy Policy",
    refunds:  "Cancellation & Refund Policy",
    delivery: "Digital Delivery Policy",
    contact:  "Contact Us (intro)",
    pricing:  "Pricing (intro)",
};

export default function AdminLegalPages() {
    const [tab, setTab] = useState("pages");
    return (
        <>
            <PageHeader title="Legal & Compliance"
                subtitle="Edit the public legal pages required for Razorpay compliance and review the Contact-form inbox."/>
            <div className="flex gap-2 border-b border-border mb-6">
                {[
                    { id: "pages",  label: "Legal pages", Icon: FileText },
                    { id: "inbox",  label: "Contact inbox", Icon: Inbox },
                ].map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`legal-tab-${t.id}`}
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.16em] border-b-2 -mb-px inline-flex items-center gap-2 ${
                            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.Icon className="w-3.5 h-3.5" strokeWidth={1.5}/>{t.label}
                    </button>
                ))}
            </div>
            {tab === "pages" ? <PagesPanel/> : <InboxPanel/>}
        </>
    );
}

function PagesPanel() {
    const [pages, setPages] = useState([]);
    const [activeSlug, setActiveSlug] = useState("terms");
    const [draft, setDraft] = useState(null);     // { title, body_md }
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/legal/pages");
            setPages(data);
            const cur = data.find(p => p.slug === activeSlug) || data[0];
            if (cur) {
                setActiveSlug(cur.slug);
                setDraft({ title: cur.title, body_md: cur.body_md });
            }
        } catch (e) { toast.error(apiError(e)); }
    }, [activeSlug]);

    useEffect(() => { load(); }, []);  // eslint-disable-line

    const pickSlug = (slug) => {
        const p = pages.find(x => x.slug === slug);
        if (!p) return;
        setActiveSlug(slug);
        setDraft({ title: p.title, body_md: p.body_md });
        setShowPreview(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/legal/pages/${activeSlug}`, draft);
            toast.success("Saved");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset this page to the Law OS default content? Your changes will be lost.")) return;
        try {
            await api.post(`/admin/legal/pages/${activeSlug}/reset`);
            toast.success("Reset to default");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const active = pages.find(p => p.slug === activeSlug);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
            <div className="border border-border bg-card divide-y divide-border" data-testid="legal-slug-list">
                {pages.map(p => (
                    <button key={p.slug} onClick={() => pickSlug(p.slug)} data-testid={`legal-slug-${p.slug}`}
                        className={`block w-full text-left px-3 py-3 hover:bg-muted ${activeSlug === p.slug ? "bg-muted" : ""}`}>
                        <div className="text-xs text-foreground">{SLUG_LABELS[p.slug] || p.slug}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            /{p.slug === "pricing" ? "pricing" : `legal/${p.slug}`}
                            {p.updated_at && <span> · edited {fmtDateTime(p.updated_at)}</span>}
                        </div>
                    </button>
                ))}
            </div>

            {draft && active && (
                <div className="space-y-3" data-testid="legal-editor">
                    <div className="flex items-center justify-between">
                        <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })}
                            data-testid="legal-title-input"
                            className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-semibold w-full max-w-md"/>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setShowPreview(v => !v)} data-testid="legal-preview-toggle"
                                className="px-3 py-2 border border-border text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                                <Eye className="w-3 h-3" strokeWidth={1.5}/>{showPreview ? "Edit" : "Preview"}
                            </button>
                            <button onClick={reset} data-testid="legal-reset"
                                className="px-3 py-2 border border-border text-muted-foreground hover:text-rose-400 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                                <RotateCcw className="w-3 h-3" strokeWidth={1.5}/>Reset
                            </button>
                            <button onClick={save} disabled={saving} data-testid="legal-save"
                                className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1 font-medium">
                                <Save className="w-3 h-3" strokeWidth={1.5}/>{saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </div>

                    {showPreview ? (
                        <div className="border border-border bg-background p-6 max-h-[70vh] overflow-y-auto" data-testid="legal-preview">
                            <h1 className="text-3xl font-semibold text-foreground tracking-tight mb-4">{draft.title}</h1>
                            <div>{renderMarkdown(draft.body_md)}</div>
                        </div>
                    ) : (
                        <textarea value={draft.body_md} rows={28}
                            onChange={e => setDraft({ ...draft, body_md: e.target.value })}
                            data-testid="legal-body-input"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-3 text-xs font-mono"/>
                    )}

                    <div className="text-[10px] text-muted-foreground">
                        Markdown supported · # headers · **bold** · [links](url) · bullet lists.
                        {active.updated_at && <> Last saved {fmtDateTime(active.updated_at)}.</>}
                    </div>
                </div>
            )}
        </div>
    );
}

function InboxPanel() {
    const [rows, setRows] = useState([]);
    const [active, setActive] = useState(null);

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/legal/contact-messages"); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/legal/contact-messages/${id}`, { status });
            toast.success("Status updated");
            await load();
            setActive(a => a && a.id === id ? { ...a, status } : a);
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
            <div className="border border-border bg-card overflow-y-auto max-h-[70vh] divide-y divide-border" data-testid="legal-inbox-list">
                {rows.length === 0 && <div className="px-3 py-8 text-center text-xs text-muted-foreground">No messages yet.</div>}
                {rows.map(r => (
                    <button key={r.id} onClick={() => setActive(r)} data-testid={`legal-inbox-row-${r.id}`}
                        className={`block w-full text-left px-3 py-3 hover:bg-muted ${active?.id === r.id ? "bg-muted" : ""}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-foreground truncate">{r.subject}</div>
                            <span className={`text-[9px] uppercase tracking-[0.16em] ${r.status === "new" ? "text-primary" : "text-muted-foreground"}`}>{r.status}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">{r.name} · {r.email}</div>
                        <div className="text-[10px] text-muted-foreground">{fmtDateTime(r.created_at)}</div>
                    </button>
                ))}
            </div>
            {active ? (
                <div className="border border-border bg-card p-5 space-y-3 max-h-[70vh] overflow-y-auto" data-testid="legal-inbox-detail">
                    <div className="text-sm text-foreground">{active.subject}</div>
                    <div className="text-[10px] text-muted-foreground">
                        {active.name} · <a href={`mailto:${active.email}`} className="text-primary">{active.email}</a>
                        {active.phone && <> · {active.phone}</>}
                        {active.ip && <> · IP {active.ip}</>}
                    </div>
                    <pre className="whitespace-pre-wrap text-xs text-muted-foreground border border-border bg-background p-3" data-testid="legal-inbox-message">{active.message}</pre>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em]">
                        {["new", "in_progress", "resolved", "spam"].map(s => (
                            <button key={s} onClick={() => updateStatus(active.id, s)} data-testid={`legal-inbox-status-${s}`}
                                className={`px-3 py-1.5 border ${active.status === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="border border-border flex items-center justify-center text-xs text-muted-foreground py-20">Pick a message on the left to read.</div>
            )}
        </div>
    );
}
